import { HTMLSelect, Icon, InputGroup } from "@blueprintjs/core";
import { useMemo, useState } from "react";
import { getElementDescendant } from "../../helpers/utils";
import { EmptyStateCard } from "../EmptyStateCard";
import {
  getDisplayValue,
  getNestedDisplayValue,
  getNestedValue,
  getRiskTone,
  type ConjunctionRiskConfig,
  type RiskTone,
} from "../conjunctions/conjunctionUiUtils";
import { type ContactsByOperatorSelection } from "../ContactsByOperatorButton";
import { FleetStatusCard } from "./FleetStatusCard";

interface FleetStatusCardListProps {
  conjunctions: unknown[];
  formatters: unknown;
  riskConfig?: ConjunctionRiskConfig;
  onContactsSelect: (selection: ContactsByOperatorSelection) => void;
  onDownload: (payload: { id: string }) => void;
}

type CardRiskFilter = "all" | RiskTone;
type CardSortBy =
  | "executionEpoch"
  | "missDistance"
  | "probability"
  | "risk"
  | "tca";
type SortDirection = "asc" | "desc";

const riskFilterOptions: Array<{ label: string; value: CardRiskFilter }> = [
  { label: "All risks", value: "all" },
  { label: "Alert", value: "danger" },
  { label: "Warning", value: "warning" },
  { label: "Info", value: "muted" },
];

const sortOptions: Array<{ label: string; value: CardSortBy }> = [
  { label: "Risk", value: "risk" },
  { label: "TCA", value: "tca" },
  { label: "Miss distance", value: "missDistance" },
  { label: "Collision probability", value: "probability" },
  { label: "Execution epoch", value: "executionEpoch" },
];

const sortDirectionOptions: Array<{ label: string; value: SortDirection }> = [
  { label: "Ascending", value: "asc" },
  { label: "Descending", value: "desc" },
];

export const getObjectValue = (
  conjunction: unknown,
  orbitElement: "target" | "chaser",
  path: string[],
) => getElementDescendant(conjunction, [orbitElement, ...path]);

export const getConjunctionId = (conjunction: unknown) =>
  getDisplayValue(getNestedValue(conjunction, ["id"]));

const getNumberValue = (value: unknown) => {
  const rawValue =
    typeof value === "object" && value !== null
      ? (value as { value?: unknown }).value
      : value;
  const numericValue =
    typeof rawValue === "number" ? rawValue : Number.parseFloat(`${rawValue}`);

  return Number.isFinite(numericValue)
    ? numericValue
    : Number.POSITIVE_INFINITY;
};

const getDateValue = (value: unknown) => {
  const parsedDate = Date.parse(getDisplayValue(value));

  return Number.isFinite(parsedDate) ? parsedDate : Number.POSITIVE_INFINITY;
};

const getRiskRank = (conjunction: unknown) => {
  const riskTone = getRiskTone(getNestedValue(conjunction, ["riskLevel"]));

  if (riskTone === "danger") return 0;
  if (riskTone === "warning") return 1;
  return 2;
};

const compareValues = (
  left: number,
  right: number,
  direction: SortDirection,
) => {
  const leftMissing = !Number.isFinite(left);
  const rightMissing = !Number.isFinite(right);

  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  const result = left - right;

  return direction === "asc" ? result : -result;
};

const getSortValue = (conjunction: unknown, sortBy: CardSortBy) => {
  switch (sortBy) {
    case "executionEpoch":
      return getDateValue(getNestedValue(conjunction, ["executionEpoch"]));
    case "missDistance":
      return getNumberValue(getNestedValue(conjunction, ["missDistance"]));
    case "probability":
      return getNumberValue(
        getNestedValue(conjunction, ["collisionProbability"]),
      );
    case "risk":
      return getRiskRank(conjunction);
    case "tca":
      return getDateValue(getNestedValue(conjunction, ["timeOfCloseApproach"]));
  }
};

const getSearchValue = (conjunction: unknown) =>
  [
    getConjunctionId(conjunction),
    getDisplayValue(getNestedValue(conjunction, ["riskLevel"])),
    getNestedDisplayValue(conjunction, ["executionEpoch"]),
    getNestedDisplayValue(conjunction, ["timeOfCloseApproach"]),
    getDisplayValue(getNestedValue(conjunction, ["operator"])),
    getDisplayValue(getNestedValue(conjunction, ["event", "eventId"])),
    getDisplayValue(getNestedValue(conjunction, ["eventId"])),
    getDisplayValue(
      getObjectValue(conjunction, "target", ["spaceObject", "name"]),
    ),
    getDisplayValue(
      getObjectValue(conjunction, "target", ["spaceObject", "noradId"]),
    ),
    getDisplayValue(
      getObjectValue(conjunction, "target", ["spaceObject", "cosparId"]),
    ),
    getDisplayValue(
      getObjectValue(conjunction, "chaser", ["spaceObject", "name"]),
    ),
    getDisplayValue(
      getObjectValue(conjunction, "chaser", ["spaceObject", "noradId"]),
    ),
    getDisplayValue(
      getObjectValue(conjunction, "chaser", ["spaceObject", "cosparId"]),
    ),
  ]
    .join(" ")
    .toLowerCase();

export const FleetStatusCardList = ({
  conjunctions,
  formatters,
  riskConfig,
  onContactsSelect,
  onDownload,
}: FleetStatusCardListProps) => {
  const [riskFilter, setRiskFilter] = useState<CardRiskFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<CardSortBy>("risk");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const filteredConjunctions = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return conjunctions
      .filter((conjunction) => {
        const riskTone = getRiskTone(
          getNestedValue(conjunction, ["riskLevel"]),
        );

        if (riskFilter !== "all" && riskTone !== riskFilter) {
          return false;
        }

        if (!normalizedSearchTerm) return true;

        return getSearchValue(conjunction).includes(normalizedSearchTerm);
      })
      .sort((left, right) => {
        const result = compareValues(
          getSortValue(left, sortBy),
          getSortValue(right, sortBy),
          sortDirection,
        );

        if (result !== 0) return result;

        return compareValues(
          getDateValue(getNestedValue(left, ["timeOfCloseApproach"])),
          getDateValue(getNestedValue(right, ["timeOfCloseApproach"])),
          "asc",
        );
      });
  }, [conjunctions, riskFilter, searchTerm, sortBy, sortDirection]);
  const hasConjunctions = conjunctions.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50/50">
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
        <div className="focusoc-form-shell flex flex-wrap items-end gap-3">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Search
            </span>
            <InputGroup
              leftIcon="search"
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Object, NORAD, event, operator..."
              value={searchTerm}
            />
          </label>

          <label className="flex min-w-[150px] flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Risk
            </span>
            <HTMLSelect
              fill
              onChange={(event) =>
                setRiskFilter(event.target.value as CardRiskFilter)
              }
              options={riskFilterOptions}
              value={riskFilter}
            />
          </label>

          <label className="flex min-w-[180px] flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Sort by
            </span>
            <HTMLSelect
              fill
              onChange={(event) => setSortBy(event.target.value as CardSortBy)}
              options={sortOptions}
              value={sortBy}
            />
          </label>

          <label className="flex min-w-[150px] flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Order
            </span>
            <HTMLSelect
              fill
              onChange={(event) =>
                setSortDirection(event.target.value as SortDirection)
              }
              options={sortDirectionOptions}
              value={sortDirection}
            />
          </label>

          <span className="mb-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
            {filteredConjunctions.length} / {conjunctions.length}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {filteredConjunctions.length === 0 ? (
          <EmptyStateCard
            icon="satellite"
            title={
              hasConjunctions
                ? "No conjunctions match the current filters"
                : "No conjunctions to show"
            }
            description={
              hasConjunctions
                ? "Adjust search, risk or sort controls to widen the list."
                : "Select another organization or wait for fleet data to be available."
            }
          />
        ) : (
          <div className="grid min-w-0 grid-cols-1 gap-3">
            {filteredConjunctions.map((conjunction, index) => (
              <FleetStatusCard
                conjunction={conjunction}
                formatters={formatters}
                riskConfig={riskConfig}
                key={`${getConjunctionId(conjunction)}-${index}`}
                onContactsSelect={onContactsSelect}
                onDownload={onDownload}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
